import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { productsAPI, aiAPI } from '@/utils/api';
import { toast } from 'sonner';
import { Sparkles, Search, ShoppingCart, Clock, Crown, LogOut } from 'lucide-react';

const Products = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [cart, setCart] = useState([]);
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  useEffect(() => {
    fetchProducts();
    const savedCart = JSON.parse(localStorage.getItem('cart') || '[]');
    setCart(savedCart);
  }, []);

  const fetchProducts = async (searchTerm = '') => {
    try {
      const response = await productsAPI.getAll({ search: searchTerm });
      setProducts(response.data);
    } catch (error) {
      toast.error('Failed to fetch products');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchProducts(search);
  };

  const addToCart = (product) => {
    const newCart = [...cart, { ...product, quantity: 1 }];
    setCart(newCart);
    localStorage.setItem('cart', JSON.stringify(newCart));
    toast.success('Added to cart!');
  };

  const calculateTimeLeft = (endTime) => {
    if (!endTime) return null;
    const diff = new Date(endTime) - new Date();
    if (diff <= 0) return 'ENDED';
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    return `${hours}h ${minutes}m`;
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-[#FAF8F5]">
      {/* Navbar */}
      <nav className="border-b border-[#E5E1DC] bg-white/90 backdrop-blur-md sticky top-0 z-50">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3 cursor-pointer" onClick={() => navigate('/')}>
              <Sparkles className="h-7 w-7 text-[#1A3A52]" strokeWidth={1.5} />
              <span className="text-2xl font-serif font-semibold text-[#2C3E50]">LuxLyx</span>
            </div>
            <div className="flex items-center space-x-4">
              {user.membership_type === 'premium' && (
                <div className="flex items-center space-x-1 bg-[#1A3A52]/5 border border-[#1A3A52]/20 rounded-full px-4 py-1">
                  <Crown className="h-4 w-4 text-[#1A3A52]" strokeWidth={1.5} />
                  <span className="text-[#1A3A52] text-sm font-medium">Premium</span>
                </div>
              )}
              <Button
                data-testid="membership-nav-btn"
                variant="ghost"
                onClick={() => navigate('/membership')}
                className="text-[#5A6C7D] hover:text-[#1A3A52] hover:bg-[#F5F3F0]"
              >
                Membership
              </Button>
              <Button
                data-testid="orders-nav-btn"
                variant="ghost"
                onClick={() => navigate('/orders')}
                className="text-[#5A6C7D] hover:text-[#1A3A52] hover:bg-[#F5F3F0]"
              >
                Orders
              </Button>
              <Button
                data-testid="cart-btn"
                onClick={() => navigate('/cart')}
                className="bg-[#1A3A52] text-white hover:bg-[#2D4F6B] font-medium relative"
              >
                <ShoppingCart className="h-5 w-5 mr-2" strokeWidth={1.5} />
                Cart ({cart.length})
              </Button>
              <Button
                data-testid="logout-btn"
                variant="ghost"
                onClick={handleLogout}
                className="text-[#5A6C7D] hover:text-[#1A3A52] hover:bg-[#F5F3F0]"
              >
                <LogOut className="h-5 w-5" strokeWidth={1.5} />
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Search */}
      <div className="bg-white border-b border-[#E5E1DC] py-8">
        <div className="container mx-auto px-6">
          <form onSubmit={handleSearch} className="max-w-2xl mx-auto">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-[#8B9AAC]" strokeWidth={1.5} />
              <Input
                data-testid="search-input"
                type="text"
                placeholder="Search curated collections..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-12 pr-4 py-6 bg-[#FAF8F5] border-[#E5E1DC] text-[#2C3E50] text-lg rounded-full focus:border-[#1A3A52]"
              />
            </div>
          </form>
        </div>
      </div>

      {/* Products Grid */}
      <div className="container mx-auto px-6 py-16">
        {loading ? (
          <div className="text-center py-20">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#1A3A52]"></div>
          </div>
        ) : products.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-[#5A6C7D] text-xl">No products found</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
            {products.map((product) => (
              <div
                key={product.id}
                data-testid={`product-card-${product.id}`}
                className="bg-white border border-[#E5E1DC] rounded-2xl overflow-hidden hover:shadow-lg transition-all cursor-pointer group"
                onClick={() => navigate(`/products/${product.id}`)}
              >
                <div className="relative aspect-square overflow-hidden bg-[#F5F3F0]">
                  <img
                    src={product.images[0] || 'https://via.placeholder.com/400'}
                    alt={product.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  {product.flash_sale_end && (
                    <div className="absolute top-4 right-4 bg-[#2C3E50] text-white px-3 py-1.5 rounded-full text-xs font-medium flex items-center space-x-1">
                      <Clock className="h-3.5 w-3.5" strokeWidth={2} />
                      <span>{calculateTimeLeft(product.flash_sale_end)}</span>
                    </div>
                  )}
                  <div className="absolute top-4 left-4 bg-[#1A3A52] text-white px-3 py-1.5 rounded-full text-xs font-semibold">
                    {Math.round(product.discount_percent)}% OFF
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="font-serif font-semibold text-lg mb-2 line-clamp-1 text-[#2C3E50]">{product.name}</h3>
                  <p className="text-[#5A6C7D] text-sm mb-4 line-clamp-2">{product.description}</p>
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <div className="text-2xl font-serif font-semibold text-[#1A3A52]">${product.current_price}</div>
                      <div className="text-sm text-[#8B9AAC] line-through">${product.mrp}</div>
                    </div>
                    <div className="text-sm text-[#8B9AAC]">
                      {product.stock} available
                    </div>
                  </div>
                  <Button
                    data-testid={`add-to-cart-${product.id}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      addToCart(product);
                    }}
                    className="w-full bg-[#1A3A52] text-white hover:bg-[#2D4F6B] font-medium"
                  >
                    Add to Cart
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Products;
